import { ChatFirestorePaths, Message } from '@quadminds/shared/stork';
import { addDoc, collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { getChatAuth, getChatFirestore } from './chat-firebase';

interface FirestoreMessage {
    text: string;
    senderType: 'OPERATOR' | 'DRIVER';
    senderId: string;
    senderName?: string;
    timestamp: string;
    readAt?: string;
}

export interface UseDriverChatOptions {
    /**
     * When true, the hook returns `mockMessages` and `sendMessage` is a no-op.
     * Set via a dev flag to work without Firestore during local development.
     */
    useMock?: boolean;
    /** Messages to return when `useMock` is true. */
    mockMessages?: Message[];
    /**
     * Called right after the message lands in Firestore, so the backend can push-notify the
     * driver. Lives here and not at the call site on purpose: the messages go straight from the
     * client to Firestore and no backend sees them, so anything that writes a message has to
     * announce it. Keeping the two together is what stops a new screen from silently skipping
     * the notification.
     */
    onMessageSent?: (roomId: string) => void;
}

const toMessage = (id: string, data: FirestoreMessage): Message => ({
    id,
    type: 'text',
    content: data.text,
    direction: data.senderType === 'OPERATOR' ? 'outgoing' : 'incoming',
    timestamp: new Date(data.timestamp),
    senderName: data.senderType === 'OPERATOR' ? data.senderName : undefined,
});

export const useDriverMessages = (roomId: string | undefined, options?: UseDriverChatOptions) => {
    const [messages, setMessages] = useState<Message[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [sendError, setSendError] = useState<string | null>(null);

    useEffect(() => {
        // Mock mode: return provided mock messages without touching Firestore
        if (options?.useMock) {
            setMessages(options.mockMessages ?? []);
            setIsLoading(false);
            return;
        }

        if (!roomId) {
            setMessages([]);
            setIsLoading(false);
            return;
        }

        let firestore: ReturnType<typeof getChatFirestore>;
        try {
            firestore = getChatFirestore();
        } catch {
            // initChatFirebase not yet called
            return;
        }

        setIsLoading(true);

        const messagesRef = collection(firestore, `${ChatFirestorePaths.ROOMS}/${roomId}/messages`);
        const q = query(messagesRef, orderBy('timestamp', 'asc'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            setMessages(snapshot.docs.map((doc) => toMessage(doc.id, doc.data() as FirestoreMessage)));
            setIsLoading(false);
        });

        return unsubscribe;
    }, [roomId, options?.useMock, options?.mockMessages]);

    const sendMessage = async (text: string, senderName?: string): Promise<void> => {
        if (options?.useMock || !roomId) return;

        setSendError(null);
        const auth = await getChatAuth();
        const senderId = auth?.currentUser?.uid;
        if (!senderId) {
            setSendError('No pudimos conectar el chat. Intentá enviar el mensaje de nuevo en unos segundos.');
            return;
        }

        const messagesRef = collection(getChatFirestore(), `${ChatFirestorePaths.ROOMS}/${roomId}/messages`);
        await addDoc(messagesRef, {
            text,
            senderType: 'OPERATOR',
            senderId,
            ...(senderName ? { senderName } : {}),
            timestamp: new Date().toISOString(),
        });

        options?.onMessageSent?.(roomId);
    };

    return { messages, sendMessage, isLoading, sendError };
};
